import { Locales } from '@magicdoorinc/date';
import { setI18n } from '@magicdoorinc/ui';
import en from '~/locales/en/translation.json';
import { applyHoaLabelOverrides } from '~/utils/hoaDemoMode';
import { markMissingTranslations } from '~/utils/i18nMissingKeyMarker';
import type { HoaLocale } from '~/utils/hoaDemoMode';

type Translation = Record<string, string>;

const LOCALE_STORAGE_KEY = 'locale';

const prepare = (translations: Translation, locale: HoaLocale): Translation =>
  markMissingTranslations(applyHoaLabelOverrides(translations, locale));

const localeSources: Record<Locales, () => Promise<Translation>> = {
  [Locales.EN_US]: async () => prepare(en, 'en'),
  [Locales.ES_MX]: async () => prepare((await import('~/locales/es/translation.json')).default, 'es'),
  [Locales.ZH_CN]: async () => prepare((await import('~/locales/zh/translation.json')).default, 'zh-cn'),
};

const loaded: Partial<Record<Locales, Translation>> = {};

const publish = () => setI18n({ locals: loaded, localStorageKey: LOCALE_STORAGE_KEY });

const isSupportedLocale = (value: string | null): value is Locales => value !== null && value in localeSources;

const preferredLocale = (): Locales => {
  const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
  return isSupportedLocale(stored) ? stored : Locales.EN_US;
};

export const loadLocale = async (locale: Locales): Promise<void> => {
  loaded[locale] = loaded[locale] ?? (await localeSources[locale]());
  publish();
};

export const initI18n = async (): Promise<void> => {
  await loadLocale(Locales.EN_US);

  const preferred = preferredLocale();
  if (preferred === Locales.EN_US) return;

  try {
    await loadLocale(preferred);
  } catch {
    publish();
  }
};
